(() => {
  'use strict';

  const storageKey = 'lkf-timeless-lane-appearance';
  let cleanup = () => {};
  let firstMount = true;

  function mountJourney(root, chapters, signal, openModal, closeModal) {
    const route = root.querySelector('[data-tl-route]');
    const host = route && route.closest('.shopify-section');
    const menu = root.querySelector('[data-tl-chapter-menu]');
    const menuButton = root.querySelector('[data-tl-chapters-toggle]');
    const header = document.querySelector('header[data-section-type="header"]') || document.querySelector('header');
    const headerHost = header && (header.closest('.header__outer-wrapper') || header);
    const chapterList = Array.from(chapters.values());
    const mobile = window.matchMedia('(max-width: 719px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = -1;
    let frame = 0;
    let disposed = false;
    let animation = null;
    let observer = null;

    function headerOffset(forJump = false) {
      if (!header) return 0;
      if (forJump) {
        const style = getComputedStyle(headerHost);
        if (style.position !== 'sticky' && style.position !== 'fixed') return 0;
        return Math.max(0, Math.min(innerHeight, (parseFloat(style.top) || 0) + header.getBoundingClientRect().height));
      }
      return Math.max(0, Math.min(innerHeight, header.getBoundingClientRect().bottom));
    }

    function jumpToChapter(chapter, instant = false) {
      const index = chapterList.indexOf(chapter);
      if (route && host) {
        setActiveChapter(index);
        host.style.height = `${route.getBoundingClientRect().height}px`;
      }
      const height = route ? route.getBoundingClientRect().height : 0;
      const target = chapter.section.getBoundingClientRect().top + window.scrollY - headerOffset(true) - height;
      chapter.heading.focus({ preventScroll: true });
      window.scrollTo({ top: Math.max(0, target), behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      history.replaceState(null, '', chapter.hash);
      scheduleUpdate();
    }

    if (!route || !host || !chapterList.length) return { jumpToChapter, cleanup: () => {} };

    const hostStyle = host.getAttribute('style');
    const routeStyle = route.getAttribute('style');
    const current = route.querySelector('[data-tl-current]');
    const previous = route.querySelector('[data-tl-previous]');
    const next = route.querySelector('[data-tl-next]');
    const stops = Array.from(route.querySelectorAll('.tl-route__stops a'));
    const menuList = menu && menu.querySelector('[data-tl-chapter-list]');
    const menuLinks = [];
    const end = root.querySelector('[id$="__tl_gallery"]') || chapterList[chapterList.length - 1].section;
    let percent;
    try { percent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'percent', maximumFractionDigits: 0 }); }
    catch (error) { percent = new Intl.NumberFormat('en', { style: 'percent', maximumFractionDigits: 0 }); }

    if (menuList) menuList.replaceChildren();
    chapterList.forEach((chapter, index) => {
      const stop = stops.find((link) => link.getAttribute('href') === chapter.hash);
      chapter.era = stop ? stop.querySelector('[data-tl-era]').textContent.trim() : '';
      if (!menuList) return;
      const item = document.createElement('li');
      const link = document.createElement('a');
      const number = document.createElement('span');
      const text = document.createElement('span');
      const title = document.createElement('strong');
      const era = document.createElement('small');
      const check = document.createElement('span');
      link.href = chapter.hash;
      number.className = 'tl-route__number';
      number.textContent = String(index + 1).padStart(2, '0');
      number.setAttribute('aria-hidden', 'true');
      text.className = 'tl-chapter-menu__text';
      title.textContent = chapter.heading.textContent.trim();
      era.textContent = chapter.era;
      check.className = 'tl-chapter-menu__check';
      check.textContent = '✓';
      check.setAttribute('aria-hidden', 'true');
      text.append(title, era);
      link.append(number, text, check);
      item.appendChild(link);
      menuList.appendChild(item);
      menuLinks.push(link);
    });

    function setActiveChapter(index) {
      if (index < 0 || index === activeIndex) return;
      const chapter = chapterList[index];
      const direction = index > activeIndex ? 1 : -1;
      route.querySelector('[data-tl-current-count]').textContent = route.dataset.tlCountTemplate
        .replace('__current__', String(index + 1).padStart(2, '0'))
        .replace('__total__', String(chapterList.length).padStart(2, '0'));
      route.querySelector('[data-tl-current-title]').textContent = chapter.heading.textContent.trim();
      route.querySelector('[data-tl-current-era]').textContent = chapter.era;
      previous.disabled = index === 0;
      next.disabled = index === chapterList.length - 1;
      [...stops, ...menuLinks].forEach((link) => {
        const target = chapters.get(link.getAttribute('href'));
        if (target === chapter) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
        link.toggleAttribute('data-tl-complete', target && chapterList.indexOf(target) < index);
      });
      if (activeIndex >= 0 && mobile.matches && !reducedMotion.matches && typeof current.animate === 'function') {
        if (animation) animation.cancel();
        animation = current.animate([
          { opacity: 0.35, transform: `translateX(${direction * 10}px)` },
          { opacity: 1, transform: 'translateX(0)' },
        ], { duration: 180, easing: 'ease-out' });
      }
      activeIndex = index;
    }

    function update() {
      frame = 0;
      if (disposed || !host.isConnected) return;
      const offset = headerOffset();
      const height = route.getBoundingClientRect().height;
      const anchor = host.getBoundingClientRect();
      const bounds = chapterList.map((chapter) => chapter.section.getBoundingClientRect());
      const endBounds = end.getBoundingClientRect();
      const endTop = end === chapterList[chapterList.length - 1].section ? endBounds.bottom : endBounds.top;
      const readingLine = offset + height + 1;
      const progress = Math.max(0, Math.min(1, (readingLine - bounds[0].top) / Math.max(1, endTop - bounds[0].top)));
      const index = Math.max(0, bounds.reduce((found, bounds, index) => bounds.top <= readingLine ? index : found, -1));
      const fixed = anchor.top <= offset + 0.5;
      const state = fixed ? (endTop <= offset ? 'after' : 'stuck') : 'before';
      host.style.height = `${height}px`;
      route.style.setProperty('--tl-route-top', `${Math.min(offset, endTop - height)}px`);
      route.style.setProperty('--tl-route-left', `${anchor.left}px`);
      route.style.setProperty('--tl-route-width', `${anchor.width}px`);
      route.style.setProperty('--tl-progress', `${progress * 100}%`);
      route.dataset.tlState = state;
      if (state === 'after') route.setAttribute('aria-hidden', 'true');
      else route.removeAttribute('aria-hidden');
      route.querySelectorAll('[data-tl-progress]').forEach((track) => track.setAttribute('aria-valuenow', String(Math.round(progress * 100))));
      route.querySelectorAll('[data-tl-progress-text]').forEach((label) => { label.textContent = percent.format(progress); });
      root.style.setProperty('--tl-header-offset', `${offset}px`);
      root.style.setProperty('--tl-route-height', `${height}px`);
      setActiveChapter(index);
    }

    function scheduleUpdate() {
      if (route && host && chapterList.length && !disposed && !frame) frame = requestAnimationFrame(update);
    }

    setActiveChapter(0);
    route.setAttribute('data-tl-enhanced', '');
    route.querySelector('.tl-route__mobile').hidden = false;
    route.querySelectorAll('[data-tl-progress], [data-tl-progress-text]').forEach((element) => { element.hidden = false; });
    update();
    previous.addEventListener('click', () => {
      if (activeIndex > 0) jumpToChapter(chapterList[activeIndex - 1]);
    }, { signal });
    next.addEventListener('click', () => {
      if (activeIndex < chapterList.length - 1) jumpToChapter(chapterList[activeIndex + 1]);
    }, { signal });
    if (menu && menuButton) {
      menuButton.addEventListener('click', () => {
        if (openModal(menu, menuButton)) menuButton.setAttribute('aria-expanded', 'true');
      }, { signal });
      mobile.addEventListener('change', () => {
        if (!mobile.matches && menu.open) closeModal(menu, stops.find((link) => link.getAttribute('href') === chapterList[activeIndex].hash));
        scheduleUpdate();
      }, { signal });
    }
    window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
    window.addEventListener('load', scheduleUpdate, { once: true, signal });
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(scheduleUpdate);
      [route, host, end, header, ...chapterList.map((chapter) => chapter.section)].filter(Boolean).forEach((element) => observer.observe(element));
    }
    if (document.fonts) document.fonts.ready.then(scheduleUpdate);

    return {
      jumpToChapter,
      cleanup: () => {
        disposed = true;
        if (frame) cancelAnimationFrame(frame);
        if (observer) observer.disconnect();
        if (animation) animation.cancel();
        if (hostStyle === null) host.removeAttribute('style');
        else host.setAttribute('style', hostStyle);
        if (routeStyle === null) route.removeAttribute('style');
        else route.setAttribute('style', routeStyle);
        route.removeAttribute('data-tl-enhanced');
        route.removeAttribute('data-tl-state');
        route.removeAttribute('aria-hidden');
        route.querySelector('.tl-route__mobile').hidden = true;
        route.querySelectorAll('[data-tl-progress], [data-tl-progress-text]').forEach((element) => { element.hidden = true; });
        root.style.removeProperty('--tl-header-offset');
        root.style.removeProperty('--tl-route-height');
      },
    };
  }

  function mount() {
    cleanup();
    const marker = document.querySelector('#main [data-tl-page]');
    if (!marker) return;

    const root = marker.closest('main');
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listeners = new AbortController();
    const { signal } = listeners;
    const dialog = root.querySelector('[data-tl-dialog]');
    const dialogBody = root.querySelector('[data-tl-dialog-body]');
    const menu = root.querySelector('[data-tl-chapter-menu]');
    const menuButton = root.querySelector('[data-tl-chapters-toggle]');
    const appearance = marker.querySelector('[data-tl-appearance]');
    let savedMode = null;
    let returnFocus = null;
    let previousOverflow = '';
    let scrollLocked = false;
    let activeModal = null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'light' || stored === 'dark') savedMode = stored;
    } catch (error) { /* A blocked storage API must not prevent page interaction. */ }

    function applyMode(mode) {
      root.setAttribute('data-timeless-lane', '');
      root.dataset.tlAppearance = mode;
      marker.querySelectorAll('[data-tl-mode]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.tlMode === mode));
      });
    }

    const systemChanged = () => {
      if (!savedMode) applyMode(media.matches ? 'dark' : 'light');
    };

    applyMode(savedMode || (media.matches ? 'dark' : 'light'));
    if (appearance) appearance.hidden = false;
    if (media.addEventListener) media.addEventListener('change', systemChanged);
    else media.addListener(systemChanged);

    marker.querySelectorAll('[data-tl-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        savedMode = button.dataset.tlMode;
        applyMode(savedMode);
        try { localStorage.setItem(storageKey, savedMode); } catch (error) { /* Session-only choice. */ }
      }, { signal });
    });

    const chapters = new Map();
    for (let number = 1; number <= 4; number += 1) {
      const key = String(number).padStart(2, '0');
      const section = root.querySelector(`[id$="__tl_chapter_${key}"]`);
      if (!section) continue;
      const heading = section.querySelector('h2');
      if (!heading) continue;
      heading.id = `tl-story-${key}`;
      heading.tabIndex = -1;
      chapters.set(`#tl-story-${key}`, { section, heading, hash: `#tl-story-${key}` });
    }

    function unlockScroll() {
      if (!scrollLocked) return;
      document.body.style.overflow = previousOverflow;
      scrollLocked = false;
    }

    function openModal(modal, trigger) {
      if (!modal || typeof modal.showModal !== 'function' || (activeModal && activeModal.open)) return false;
      returnFocus = trigger;
      previousOverflow = document.body.style.overflow;
      modal.showModal();
      activeModal = modal;
      document.body.style.overflow = 'hidden';
      scrollLocked = true;
      const first = modal.querySelector('[aria-current="step"]') || modal.querySelector('button');
      if (first) first.focus({ preventScroll: true });
      return true;
    }

    function closeModal(modal, focusTarget) {
      if (!modal || !modal.open) return;
      if (focusTarget) returnFocus = focusTarget;
      modal.close();
      unlockScroll();
    }

    const journey = mountJourney(root, chapters, signal, openModal, closeModal);

    function openStory(chapter, trigger) {
      if (!dialog || typeof dialog.showModal !== 'function' || (activeModal && activeModal.open)) return false;
      const originalImage = chapter.section.querySelector('img');
      const content = chapter.section.querySelector('.image-with-text-split__text-container-inner');
      if (!originalImage || !content) return false;
      dialogBody.replaceChildren();
      const figure = document.createElement('div');
      figure.className = 'tl-dialog__media';
      const image = document.createElement('img');
      image.src = originalImage.currentSrc || originalImage.src;
      image.alt = originalImage.alt;
      image.width = originalImage.naturalWidth || originalImage.width;
      image.height = originalImage.naturalHeight || originalImage.height;
      image.decoding = 'async';
      figure.appendChild(image);
      const story = document.createElement('div');
      story.className = 'tl-dialog__story';
      const caption = Array.from(content.querySelectorAll('.section-blocks__text')).pop();
      [chapter.heading, caption].filter(Boolean).forEach((block) => {
        const clone = block.cloneNode(true);
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        if (clone.matches('.section-blocks__heading')) {
          clone.id = 'tl-dialog-title';
          clone.removeAttribute('tabindex');
        }
        story.appendChild(clone);
      });
      dialogBody.append(figure, story);
      return openModal(dialog, trigger);
    }

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href^="#tl-story-"]');
      if (!link) return;
      const chapter = chapters.get(link.getAttribute('href'));
      if (!chapter) return;
      if (link.closest('[id$="__tl_gallery"]') && openStory(chapter, link)) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (menu && menu.open) closeModal(menu, chapter.heading);
      journey.jumpToChapter(chapter);
    }, { signal });

    if (dialog) {
      dialog.id = 'tl-story-dialog';
      root.querySelectorAll('[id$="__tl_gallery"] a[href^="#tl-story-"]').forEach((link) => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', dialog.id);
      });
    }

    [dialog, menu].filter(Boolean).forEach((modal) => {
      const closeButton = modal.querySelector('[data-tl-dialog-close], [data-tl-chapter-close]');
      if (closeButton) closeButton.addEventListener('click', () => closeModal(modal), { signal });
      modal.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const targets = [...modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => element.getClientRects().length > 0);
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (!first) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, { signal });
      modal.addEventListener('click', (event) => {
        if (event.target !== modal) return;
        const bounds = modal.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeModal(modal);
      }, { signal });
      modal.addEventListener('close', () => {
        if (modal.open) return;
        if (activeModal === modal) {
          unlockScroll();
          if (returnFocus && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
          returnFocus = null;
          activeModal = null;
        }
        if (modal === dialog) dialogBody.replaceChildren();
        if (modal === menu && menuButton) menuButton.setAttribute('aria-expanded', 'false');
      }, { signal });
    });

    const currentHash = chapters.get(window.location.hash);
    let hashFrame = 0;
    let initialJumpCancelled = false;
    if (firstMount && currentHash) {
      const positionInitialChapter = () => {
        Promise.resolve(document.fonts && document.fonts.ready).then(() => {
          if (initialJumpCancelled) return;
          hashFrame = requestAnimationFrame(() => {
            hashFrame = requestAnimationFrame(() => {
              if (!initialJumpCancelled) journey.jumpToChapter(currentHash, true);
            });
          });
        });
      };
      if (document.readyState === 'complete') positionInitialChapter();
      else window.addEventListener('load', positionInitialChapter, { once: true, signal });
      ['pointerdown', 'touchstart', 'wheel', 'keydown', 'hashchange'].forEach((event) => {
        window.addEventListener(event, () => { initialJumpCancelled = true; }, { once: true, passive: true, signal });
      });
    }
    firstMount = false;

    cleanup = () => {
      [dialog, menu].filter(Boolean).forEach((modal) => { if (modal.open) modal.close(); });
      unlockScroll();
      returnFocus = null;
      activeModal = null;
      initialJumpCancelled = true;
      if (hashFrame) cancelAnimationFrame(hashFrame);
      journey.cleanup();
      listeners.abort();
      if (media.removeEventListener) media.removeEventListener('change', systemChanged);
      else media.removeListener(systemChanged);
    };
  }

  if (window.timelessLanePage) {
    window.timelessLanePage.mount();
    return;
  }
  window.timelessLanePage = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', () => {
    cleanup();
    requestAnimationFrame(mount);
  });
})();
